import { useEffect, useMemo, useRef, useState } from "react";

const valuesEqual = (a, b) => {
  const aDate = a instanceof Date ? a : a != null ? new Date(a) : null;
  const bDate = b instanceof Date ? b : b != null ? new Date(b) : null;

  const aValid = aDate instanceof Date && !Number.isNaN(aDate.getTime());
  const bValid = bDate instanceof Date && !Number.isNaN(bDate.getTime());

  if (aValid && bValid) return aDate.getTime() === bDate.getTime();
  return (a ?? null) === (b ?? null);
};

const defaultIsEmpty = (v) =>
  v === null || v === undefined || v === "";

export default function useBulkColumnEdit({
  rows = [],
  columnDefs = [],
  getRowKey,
  getValue,
  applyChanges,
}) {
  const [bulk, setBulk] = useState({
    open: false,
    anchorPosition: null,
    field: null,
    mode: "all",
    value: null,
  });

  const [previewKeys, setPreviewKeys] = useState(() => new Set());

  const dateInputRef = useRef(null);
  const numberInputRef = useRef(null);
  const textInputRef = useRef(null);

  const findCol = (field) =>
    columnDefs.find((c) => c.field === field);

  const safeGetValue = (row, field) => {
    const col = findCol(field);
    if (col?.getValue) return col.getValue(row);
    if (getValue) return getValue(row, field);
    return row?.[field];
  };

  const isEmptyForCol = (col, v) =>
    col?.isEmpty ? col.isEmpty(v) : defaultIsEmpty(v);

  const openBulkEditor = ({ anchorEl, field }) => {
    if (!anchorEl || !field) return;

    const col = findCol(field);
    if (!col) return;

    const rect = anchorEl.getBoundingClientRect();

    setBulk({
      open: true,
      anchorPosition: {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      },
      field,
      mode: "all",
      value: null,
    });
  };

  const closeBulkEditor = () => {
    setBulk({
      open: false,
      anchorPosition: null,
      field: null,
      mode: "all",
      value: null,
    });
    setPreviewKeys(new Set());
  };

  useEffect(() => {
    if (!bulk.open || !bulk.field) {
      setPreviewKeys(new Set());
      return;
    }

    const col = findCol(bulk.field);
    if (!col) return;

    const keys = new Set();
    const nextVal = bulk.value ?? null;

    rows.forEach((row) => {
      const rowKey = String(getRowKey(row));
      const current = safeGetValue(row, bulk.field) ?? null;

      const shouldSet =
        bulk.mode === "all"
          ? true
          : isEmptyForCol(col, current);

      if (!shouldSet) return;

      if (!valuesEqual(current, nextVal)) {
        keys.add(rowKey);
      }
    });

    setPreviewKeys(keys);
  }, [bulk.open, bulk.field, bulk.mode, bulk.value, rows]); // eslint-disable-line

  useEffect(() => {
    if (!bulk.open) return;

    const t = setTimeout(() => {
      const col = findCol(bulk.field);
      if (!col) return;

      if (col.type === "date")
        dateInputRef.current?.focus?.();

      if (col.type === "number")
        numberInputRef.current?.focus?.();

      if (col.type === "text" || col.type === "select")
        textInputRef.current?.focus?.();
    }, 0);

    return () => clearTimeout(t);
  }, [bulk.open, bulk.field]); // eslint-disable-line

  const applySingleBulk = async () => {
    if (!bulk.field) return;

    const field = bulk.field;
    const col = findCol(field);
    if (!col) return;

    const affectedRowKeys = [];
    const nextVal = bulk.value ?? null;

    rows.forEach((row) => {
      const rowKey = String(getRowKey(row));
      const current = safeGetValue(row, field) ?? null;

      const shouldSet =
        bulk.mode === "all"
          ? true
          : isEmptyForCol(col, current);

      if (!shouldSet) return;

      if (!valuesEqual(current, nextVal)) {
        affectedRowKeys.push(rowKey);
      }
    });

    if (!affectedRowKeys.length) {
      closeBulkEditor();
      return;
    }

    await applyChanges?.({
      field,
      value: nextVal,
      rowKeys: affectedRowKeys,
    });

    closeBulkEditor();
  };

  const onEditorKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeBulkEditor();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      applySingleBulk();
    }
  };

  const activeCol = bulk.field
    ? findCol(bulk.field)
    : null;

  const inputRefs = {
    date: dateInputRef,
    number: numberInputRef,
    text: textInputRef,
    select: textInputRef,
  };

  return {
    bulk,
    setBulk,
    activeCol,
    previewKeys,
    openBulkEditor,
    closeBulkEditor,
    applySingleBulk,
    onEditorKeyDown,
    inputRefs,
  };
}